import moment from 'moment';
import { BarElement, CategoryScale, Chart as ChartJS, LinearScale, Tooltip } from 'chart.js';
import React, { useEffect, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';



import arrowDownIcon from '../../../assets/img/icons/arrow-down.svg';
import {
  HourlyEstimatedVsCompletedChartContainer,
  HourlyEstimatedVsCompletedChartWrapper,
  HourlyEstimatedVsCompletedContainer,
  HourlyEstimatedVsCompletedHeader,
  HourlyEstimatedVsCompletedLegend,
  HourlyEstimatedVsCompletedLegendDot,
  HourlyEstimatedVsCompletedLegendItem,
  HourlyEstimatedVsCompletedLegendText,
  HourlyEstimatedVsCompletedLegendValue,
  HourlyEstimatedVsCompletedSelect,
  HourlyEstimatedVsCompletedSelectIcon,
  HourlyEstimatedVsCompletedSelectText,
  HourlyEstimatedVsCompletedTitle,
  HourlySkeletonTitle,
  HourlySkeletonSelect,
  HourlySkeletonLegend,
  HourlySkeletonLegendItem,
  HourlySkeletonLegendDot,
  HourlySkeletonLegendText,
  HourlySkeletonChart,
  HourlySkeletonChartBars,
  HourlySkeletonBarGroup,
  HourlySkeletonBar,
  HourlySkeletonBarLabel,
  TopPerformersDropdownContainer,
  TopPerformersDropdownOption,
} from "../../DashBoardNew/dashBoardNewStyles";
import ButtonHelpTooltip from 'components/Tooltip/ButtonHelpTooltip';


// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip
);

/**
 * HourlyEstimatedVsCompletedChart - Compares estimated hours vs completed hours
 * For week view: 7 days with dual bars (estimated vs completed)
 * For month view: 4 weeks with dual bars (estimated vs completed)
 */
const formatHours = (value) => `${Intl.NumberFormat('en-US').format(value)}h`;

const sumValues = (values = []) => values.reduce((acc, curr) => acc + Number(curr || 0), 0);

const HourlyEstimatedVsCompletedChart = ({
  getDashboardEstimation,
  estimationIsLoading,
  estimationData,
  selectedOrganization,
}) => {
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('This week');
  const [chartDataState, setChartDataState] = useState({
    labels: [],
    estimated: [],
    actual: [],
    period: 'week',
  });
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);

  // Call API when period or organization changes
  useEffect(() => {
    if (selectedOrganization?.id && getDashboardEstimation) {
      let startTime, endTime;

      if (selectedPeriod === 'This week') {
        startTime = moment().startOf('week').format('YYYY-MM-DD');
        endTime = moment().endOf('week').format('YYYY-MM-DD');
      } else if (selectedPeriod === 'This month') {
        startTime = moment().startOf('month').format('YYYY-MM-DD');
        endTime = moment().endOf('month').format('YYYY-MM-DD');
      }

      const payload = {
        organization_id: selectedOrganization.id,
        start_time: startTime,
        end_time: endTime,
      };

      getDashboardEstimation(payload);
    }
  }, [selectedOrganization, selectedPeriod, getDashboardEstimation]);

  // Update chart data when API data changes
  useEffect(() => {
    if (estimationData) {
      setChartDataState({
        labels: estimationData.labels || [],
        estimated: estimationData.estimated_time || [],
        actual: estimationData.actual_time || [],
        period: estimationData.period || 'week',
      });
    } else {
      setChartDataState({
        labels: [],
        estimated: [],
        actual: [],
        period: 'week',
      });
    }
  }, [estimationData]);

  // Determine if week or month view based on API response
  const isWeekView = chartDataState.period === 'week';

  // Calculate max value for Y axis
  const allValues = [...chartDataState.estimated, ...chartDataState.actual];
  const maxValue = Math.max(...allValues, 0);
  const yAxisMax = Math.ceil(maxValue / 5) * 5 || (isWeekView ? 25 : 100);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsSelectOpen(false);
      }
    };

    if (isSelectOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSelectOpen]);

  const handleSelectClick = () => {
    setIsSelectOpen(!isSelectOpen);
  };

  const handlePeriodSelect = (period) => {
    setSelectedPeriod(period);
    setIsSelectOpen(false);
  };

  const totalEstimated = sumValues(chartDataState.estimated);
  const totalCompleted = sumValues(chartDataState.actual);

  // Chart data configuration
  const chartData = {
    labels: chartDataState.labels,
    datasets: [
      {
        label: 'Estimated',
        data: chartDataState.estimated,
        backgroundColor: '#CBD5E1', // gray for estimated
        borderRadius: 0,
        barThickness: 22,
        maxBarThickness: 22,
        categoryPercentage: 0.7,
        barPercentage: 0.8,
        borderColor: '#F5F7FF',
        borderWidth: 2,
        borderSkipped: false,
      },
      {
        label: 'Completed',
        data: chartDataState.actual,
        backgroundColor: '#8DB1F7', // blue for completed
        borderRadius: 0,
        barThickness: 22,
        maxBarThickness: 22,
        categoryPercentage: 0.7,
        barPercentage: 0.8,
        borderColor: '#F5F7FF',
        borderWidth: 2,
        borderSkipped: false,
      },
    ],
  };

  // Custom plugin to display values on top of bars
  const valueLabelPlugin = {
    id: 'valueLabel',
    afterDatasetsDraw: (chart) => {
      const { ctx, data, scales } = chart;
      ctx.save();
      ctx.font = '400 11px Mona Sans, sans-serif';
      ctx.fillStyle = '#353a46';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';

      data.datasets.forEach((dataset, datasetIndex) => {
        dataset.data.forEach((value, index) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          const bar = meta.data[index];
          if (bar) {
            const x = bar.x;
            const y = bar.y;
            ctx.fillText(value.toString(), x, y - 4);
          }
        });
      });

      ctx.restore();
    },
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      x: {
        border: {
          display: true,
          color: '#d3e0fc',
          width: 2,
        },
        grid: {
          display: false,
        },
        ticks: {
          color: '#353a46',
          font: {
            family: 'Mona Sans, sans-serif',
            size: 12,
          },
        },
      },
      y: {
        border: {
          display: true,
          color: '#d3e0fc',
          width: 2,
        },
        min: 0,
        max: yAxisMax,
        grid: {
          color: '#e9f0fe',
          lineWidth: 1,
          drawTicks: false,
        },
        ticks: {
          color: '#666e7f',
          font: {
            family: 'Mona Sans, sans-serif',
            size: 12,
          },
          stepSize: isWeekView ? 5 : Math.ceil(yAxisMax / 5),
          callback: (value) => value,
        },
        beginAtZero: true,
      },
    },
    layout: {
      padding: {
        top: 20,
        bottom: 0,
        left: 0,
        right: 0,
      },
    },
  };

  // Render skeleton loading state
  if (estimationIsLoading) {
    return (
      <HourlyEstimatedVsCompletedContainer>
        <HourlyEstimatedVsCompletedHeader>
          <HourlySkeletonTitle />
          <HourlySkeletonSelect />
        </HourlyEstimatedVsCompletedHeader>

        <HourlySkeletonLegend>
          <HourlySkeletonLegendItem>
            <HourlySkeletonLegendDot />
            <HourlySkeletonLegendText />
          </HourlySkeletonLegendItem>
          <HourlySkeletonLegendItem>
            <HourlySkeletonLegendDot />
            <HourlySkeletonLegendText />
          </HourlySkeletonLegendItem>
        </HourlySkeletonLegend>

        <HourlySkeletonChart>
          <HourlySkeletonChartBars>
            {Array.from({ length: 7 }).map((_, index) => (
              <div key={`skeleton-bar-group-${index}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '48px' }}>
                <HourlySkeletonBarGroup>
                  <HourlySkeletonBar style={{ height: `${30 + index * 8}%` }} />
                  <HourlySkeletonBar style={{ height: `${40 + index * 6}%` }} />
                </HourlySkeletonBarGroup>
                <HourlySkeletonBarLabel />
              </div>
            ))}
          </HourlySkeletonChartBars>
        </HourlySkeletonChart>
      </HourlyEstimatedVsCompletedContainer>
    );
  }

  return (
    <ButtonHelpTooltip transform='translateX(-50%) translateY(60%)' enabled={true} text="This chart compares the total estimated hours vs total completed hours for the selected period. Use the dropdown to switch between weekly and monthly views.">
    <HourlyEstimatedVsCompletedContainer>
      <HourlyEstimatedVsCompletedHeader>
        <HourlyEstimatedVsCompletedTitle>Hourly Estimated vs Completed</HourlyEstimatedVsCompletedTitle>
        <HourlyEstimatedVsCompletedSelect ref={selectRef} onClick={handleSelectClick}>
          <HourlyEstimatedVsCompletedSelectText>{selectedPeriod}</HourlyEstimatedVsCompletedSelectText>
          <HourlyEstimatedVsCompletedSelectIcon src={arrowDownIcon} alt="Dropdown" />
          {isSelectOpen && (
            <TopPerformersDropdownContainer ref={dropdownRef}>
              {['This week', 'This month'].map((option) => (
                <TopPerformersDropdownOption
                  key={option}
                  isSelected={option === selectedPeriod}
                  onClick={() => handlePeriodSelect(option)}
                >
                  {option}
                </TopPerformersDropdownOption>
              ))}
            </TopPerformersDropdownContainer>
          )}
        </HourlyEstimatedVsCompletedSelect>
      </HourlyEstimatedVsCompletedHeader>

      <HourlyEstimatedVsCompletedLegend>
        <HourlyEstimatedVsCompletedLegendItem>
          <HourlyEstimatedVsCompletedLegendDot color="#CBD5E1" />
          <HourlyEstimatedVsCompletedLegendText>
            Estimated :
            <HourlyEstimatedVsCompletedLegendValue>{formatHours(totalEstimated)}</HourlyEstimatedVsCompletedLegendValue>
          </HourlyEstimatedVsCompletedLegendText>
        </HourlyEstimatedVsCompletedLegendItem>
        <HourlyEstimatedVsCompletedLegendItem>
          <HourlyEstimatedVsCompletedLegendDot color="#8DB1F7" />
          <HourlyEstimatedVsCompletedLegendText>
            Completed :
            <HourlyEstimatedVsCompletedLegendValue>{formatHours(totalCompleted)}</HourlyEstimatedVsCompletedLegendValue>
          </HourlyEstimatedVsCompletedLegendText>
        </HourlyEstimatedVsCompletedLegendItem>
      </HourlyEstimatedVsCompletedLegend>

      <HourlyEstimatedVsCompletedChartContainer>
        <HourlyEstimatedVsCompletedChartWrapper>
          {chartDataState.labels.length > 0 ? (
            <Bar
              data={chartData}
              options={chartOptions}
              plugins={[valueLabelPlugin]}
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              No data available
            </div>
          )}
        </HourlyEstimatedVsCompletedChartWrapper>
      </HourlyEstimatedVsCompletedChartContainer>
    </HourlyEstimatedVsCompletedContainer>
    </ButtonHelpTooltip>
  );
};

export default HourlyEstimatedVsCompletedChart;